import { useMemo } from "react";
import { MousePointerClick } from "lucide-react";
import {
  ActivityTimeline,
  Button,
  DescriptionItem,
  DescriptionList,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  type TimelineEvent,
} from "@fox1ck-org/ui";
import { ConversionStatus, type Conversion } from "@gen/tracker/v1/conversion_pb";

import { ConversionStatusBadge } from "@/components/canonical/conversion-status-badge";
import { getLocale, useT } from "@/lib/i18n";
import { formatAmount, formatMoney } from "@/lib/format";
import { formatStamp, parseDecimalString } from "@/lib/utils";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { conversionStatusText, useConversionTimeline } from "@/lib/services/conversion-service";
import { useGetClick } from "@/lib/services/click-service";
import { useAllAffiliateNetworks } from "@/lib/services/network-service";
import { useOffersByIds } from "@/lib/services/offer-service";
import { useLandersByIds } from "@/lib/services/lander-service";
import { useListGoals } from "@/lib/services/goal-service";
import { clickGeo } from "@/lib/geo";

export interface ConversionDetailDrawerProps {
  conversion: Conversion | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** «Открыть клик»: the screen opens the click's panel. */
  onOpenClick?: (clickId: string) => void;
}

/**
 * Конверсия (доска CampaignConversions): how its status moved, what the
 * partner sent, and where the lead came from — the click, the ad, the lander,
 * the place and the device. Read-only: a conversion is what a partner said.
 *
 * There is no «повторить постбек в источник» here (решение 07): the tracker
 * sends nothing to a source; conversions reach Facebook through vibe-fb's CAPI.
 */
export function ConversionDetailDrawer({ conversion, open, onOpenChange, onOpenClick }: ConversionDetailDrawerProps) {
  const t = useT();
  const networks = useAllAffiliateNetworks();
  const offers = useOffersByIds(conversion?.offerId ? [conversion.offerId] : []);
  const goals = useListGoals(true);
  const c = conversion;
  const network = networks.data?.items.find((n) => n.id === c?.networkId)?.name;
  const offer = offers.data?.get(c?.offerId ?? "")?.name;
  const goal = goals.data?.find((g) => g.key === c?.goalKey);
  const payout = c?.payout?.amount ? formatAmount(parseDecimalString(c.payout.amount), c.payout.currency || "USD") : undefined;
  const meta = [network, offer, payout ? t("conv.meta.payout", { payout }) : undefined, goal ? t("conv.meta.goal", { goal: goal.displayName || goal.key }) : undefined]
    .filter(Boolean)
    .join(" · ");

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        closeLabel={t("panel.close")}
        className="flex w-115 max-w-[calc(100vw_-_--spacing(12))] flex-col gap-0 p-0 sm:max-w-none"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <SheetHeader className="shrink-0 gap-1.5 border-b border-border px-5 pb-3 pt-4 text-left">
          <div className="flex min-w-0 items-center gap-2 pr-8">
            <SheetTitle className="truncate font-mono text-section-title">{t("conv.title", { id: c?.txid || c?.clickId.slice(0, 8) || "" })}</SheetTitle>
            {c ? <ConversionStatusBadge status={c.status} /> : null}
          </div>
          {meta ? <SheetDescription className="text-xs text-muted-foreground">{meta}</SheetDescription> : null}
        </SheetHeader>
        <SheetBody className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-4">{c ? <ConversionBody c={c} open={open} /> : null}</SheetBody>
        {c?.clickId && onOpenClick ? (
          <SheetFooter className="shrink-0 flex-row items-center gap-2 border-t border-border px-5 py-3 sm:justify-start">
            <Button variant="secondary" size="sm" onClick={() => onOpenClick(c.clickId)}>
              <MousePointerClick className="size-4" strokeWidth={1.5} /> {t("conv.openClick")}
            </Button>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function Heading({ children }: { children: string }) {
  return <h3 className="text-label text-muted-foreground">{children}</h3>;
}

function ConversionBody({ c, open }: { c: Conversion; open: boolean }) {
  const t = useT();
  const tz = useReportTimezone();
  const timeline = useConversionTimeline(c.dedupKey || undefined, open);
  const click = useGetClick(open && c.clickId ? c.clickId : undefined);
  const landers = useLandersByIds(click.data?.landerId ? [click.data.landerId] : []);

  const steps = useMemo<TimelineEvent[]>(() => {
    const events = timeline.data ?? [];
    let prevTs = "";
    return events
      // An idempotent retry repeats the status it confirms; the story is the moves.
      .filter((e, i) => i === 0 || e.status !== events[i - 1]!.status)
      .map((e, i) => {
        const gap = prevTs ? (Date.parse(e.ts) - Date.parse(prevTs)) / 1000 : undefined;
        prevTs = e.ts;
        const usd = e.reportingPayoutUsd ? parseDecimalString(e.reportingPayoutUsd) : undefined;
        const note =
          i === 0
            ? t("conv.step.first")
            : [
                t("conv.step.byPostback"),
                gap !== undefined ? t("conv.step.after", { gap: humanGap(gap, t) }) : "",
                e.status === ConversionStatus.APPROVED && usd ? `+${formatMoney(usd, { unit: true })} USD` : "",
              ]
                .filter(Boolean)
                .join(" · ");
        return {
          id: `${i}-${e.ts}`,
          kind: "status",
          at: e.ts,
          title: <b className="font-semibold">{i === 0 ? t("conv.step.lead") : conversionStatusText(e.status)}</b>,
          description: note,
          markerClassName: e.status === ConversionStatus.APPROVED ? "bg-ok" : e.status === ConversionStatus.REJECTED ? "bg-destructive" : "bg-primary",
        };
      });
  }, [timeline.data, t]);

  const k = click.data;
  const leadAfter = k?.ts && (c.leadTs || c.ts) ? (Date.parse(c.leadTs || c.ts) - Date.parse(k.ts)) / 1000 : undefined;
  const lander = landers.data?.get(k?.landerId ?? "")?.name;
  const raw = c.rawPayload.trim();

  return (
    <>
      <section className="flex flex-col gap-2">
        <Heading>{t("conv.history")}</Heading>
        {timeline.isLoading ? (
          <Skeleton className="h-24" />
        ) : (
          <ActivityTimeline events={steps} groupBy="none" locale={getLocale()} emptyMessage={t("conv.history.none")} labels={{ ariaLabel: t("conv.history") }} />
        )}
      </section>
      <section className="flex flex-col gap-2">
        <Heading>{t("conv.raw")}</Heading>
        {raw ? (
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-xs leading-relaxed">
            {formatRaw(raw)}
          </pre>
        ) : (
          <p className="text-sm text-muted-foreground">{t("conv.raw.none")}</p>
        )}
        <p className="text-xs text-muted-foreground">
          {t("conv.raw.accepted", { at: formatStamp(c.ts, tz), status: conversionStatusText(c.status).toLowerCase() })}
          {c.matchSource ? ` · ${t("conv.raw.match", { source: c.matchSource })}` : ""}
        </p>
      </section>
      <section className="flex flex-col gap-2">
        <Heading>{t("conv.origin")}</Heading>
        {click.isLoading ? (
          <Skeleton className="h-28" />
        ) : (
          <DescriptionList>
            <DescriptionItem
              label={t("conv.origin.click")}
              value={
                <span className="font-mono text-sm">
                  {k ? `${formatStamp(k.ts, tz)}${leadAfter !== undefined && leadAfter >= 0 ? `, ${t("conv.origin.leadAfter", { gap: humanGap(leadAfter, t) })}` : ""}` : t("conv.origin.noClick")}
                </span>
              }
            />
            <DescriptionItem label={t("conv.origin.ad")} value={c.creativeId || k?.creativeId ? <span className="font-mono text-sm">{c.creativeId || k?.creativeId}</span> : undefined} />
            <DescriptionItem label={t("conv.origin.campaignAd")} value={c.adCampaignId ? <span className="font-mono text-sm">{c.adCampaignId}</span> : undefined} />
            <DescriptionItem label={t("conv.origin.lander")} value={lander || undefined} />
            <DescriptionItem label={t("conv.origin.geo")} value={clickGeo({ country: k?.country || c.country, region: k?.region, city: k?.city }) || undefined} />
            <DescriptionItem label={t("conv.origin.device")} value={[k?.os, k?.device, k?.browser].filter(Boolean).join(" · ") || undefined} />
          </DescriptionList>
        )}
      </section>
    </>
  );
}

/** «subid=…&status=approved» → one parameter per line, the way the board prints it. Pure. */
export function formatRaw(raw: string): string {
  const q = raw.startsWith("?") ? raw.slice(1) : raw;
  if (!q.includes("=") || q.startsWith("{") || q.startsWith("[")) return raw;
  return q
    .split("&")
    .map((p, i) => (i === 0 ? `?${p}` : `&${p}`))
    .join("\n");
}

/** Seconds as «1 мин 51 с», «2 дня 18 ч». */
export function humanGap(seconds: number, t: ReturnType<typeof useT>): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return t("gap.s", { s });
  if (s < 3600) return t("gap.ms", { m: Math.floor(s / 60), s: s % 60 });
  if (s < 86_400) return t("gap.hm", { h: Math.floor(s / 3600), m: Math.floor((s % 3600) / 60) });
  return t("gap.dh", { d: Math.floor(s / 86_400), h: Math.floor((s % 86_400) / 3600) });
}
